import URI from 'urijs';
import Vue from 'vue';
import { status, orderBy, sponsors, currencySymbols } from '@/config/settlements';
import { config } from '@/plugins/env.config';
import { decamelizeKeys } from 'humps';

async function getSettlements (filters, attemptNumber = 1){
    const getSettlementsUri = new URI(config.apiBaseUrl)
    getSettlementsUri.segment('settlements')
    getSettlementsUri.segment('v3')
    getSettlementsUri.segment('settlements')

    if(filters.createdFrom){
        getSettlementsUri.addSearch('date_from', filters.createdFrom)
    }

    if(filters.createdTo){
        getSettlementsUri.addSearch('date_to', filters.createdTo)
    }

    if(filters.sponsorSlug){
        getSettlementsUri.addSearch('sponsor_slug', filters.sponsorSlug)
    }

    if(filters.codeSearchValue){
        getSettlementsUri.addSearch('settlement_id', filters.codeSearchValue)
    }

    getSettlementsUri.addSearch('page', filters.page)
    getSettlementsUri.addSearch('size', filters.size)
    
    try {
        return await Vue.prototype.$http.get(getSettlementsUri.toString())
    } catch (error) {
        const maxAttempts = config.getTokenMaxAttempts
        const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

        const response = error.response
        const status = response.status

        if (status === 500 && attemptNumber < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
            return getSettlements(filters, attemptNumber + 1)
        }

        throw error
    }
}

async function getSettlementDetail (filters, attemptNumber = 1){
    const settlementDetailURI = new URI(config.apiBaseUrl)
    settlementDetailURI.segment('settlements')
    settlementDetailURI.segment('v3')
    settlementDetailURI.segment('settlements')
    settlementDetailURI.segment(filters.settlementId)
    settlementDetailURI.segment('transactions')

    if(filters.searchValue){
        settlementDetailURI.addSearch('authorization_code', filters.searchValue)
    }

    settlementDetailURI.addSearch('page', filters.page)
    settlementDetailURI.addSearch('size', filters.size)

    try{
        return await Vue.prototype.$http.get(settlementDetailURI.toString())
    }
    catch(error){
        const maxAttempts = config.getTokenMaxAttempts
        const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

        const response = error.response
        const status = response.status

        if (status === 500 && attemptNumber < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
            return getSettlementDetail(filters, attemptNumber + 1)
        }

        throw error
    }
}

function getStatusText(statusToFind) {
    let itemFound = status.find(status => {
        if(status.value === statusToFind ){
            return status.text
        }
    })

    if(itemFound){
        return itemFound.text
    }
    return ''
}

function getSponsorText(sponsorToFind) {
    let itemFound = sponsors.find(sponsor => {
        if(sponsor.value === sponsorToFind){
            return sponsor.text
        }
    })

    if(itemFound){
        return itemFound.text
    }
    return ''
}

function sanitizeAmount(currencyCode, amount, notNull){
    let localeAmount = 0;

    if(amount){
        localeAmount = amount.toLocaleString(undefined, {minimumFractionDigits: 2})
    }
    else if(notNull){
        localeAmount = "0.00"
    }
    else{
        return ''
    }

    let currencyConfig = currencySymbols.find(symbol => symbol.code === currencyCode)
    return `${currencyCode ? currencyCode : ''} ${currencyConfig ? currencyConfig.symbol : ''}${localeAmount}`
}

async function getPendingAuthorizations (filters, attemptNumber = 1){
    const pendingAuthorizationsURI = new URI(config.apiBaseUrl)
    pendingAuthorizationsURI.segment('settlements')
    pendingAuthorizationsURI.segment('v3')
    pendingAuthorizationsURI.segment('settlements')
    pendingAuthorizationsURI.segment('pending-authorizations')

    pendingAuthorizationsURI.addSearch('sponsor_slug', filters.sponsorSlug)
    pendingAuthorizationsURI.addSearch('date_from', filters.dateFrom)
    pendingAuthorizationsURI.addSearch('date_to', filters.dateTo)
    pendingAuthorizationsURI.addSearch('page', filters.page)
    pendingAuthorizationsURI.addSearch('size', filters.size)

    try{
        return await Vue.prototype.$http.get(pendingAuthorizationsURI.toString())
    }
    catch(error){
        const maxAttempts = config.getTokenMaxAttempts
        const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

        const response = error.response
        const status = response.status

        if (status === 500 && attemptNumber < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
            return getPendingAuthorizations(filters, attemptNumber + 1)
        }

        throw error
    }
}

async function setSettlementId (sponsorSlug, attemptNumber = 1){
    const setSettlementIdURI = new URI(config.apiBaseUrl)
    setSettlementIdURI.segment('settlements')
    setSettlementIdURI.segment('v3')
    setSettlementIdURI.segment('settlements')

    try{
        return await Vue.prototype.$http.post(setSettlementIdURI.toString(), { sponsor_slug : sponsorSlug})
    }catch(error){
        const maxAttempts = config.getTokenMaxAttempts
        const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

        const response = error.response
        const status = response.status

        if (status === 500 && attemptNumber < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
            return setSettlementId(sponsorSlug, attemptNumber + 1)
        }
        throw error
    }
}

async function setClaims(body, attemptNumber = 1){
    const setClaimsURI = new URI(config.apiBaseUrl)
    setClaimsURI.segment('settlements')
    setClaimsURI.segment('v3')
    setClaimsURI.segment('settlements')
    setClaimsURI.segment(body.settlementId)
    setClaimsURI.segment('claims')

    body = decamelizeKeys(body)

    try{
        return await Vue.prototype.$http.post(setClaimsURI.toString(), body.params);
    }catch(error){
        const maxAttempts = config.getTokenMaxAttempts
        const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

        const response = error.response
        const status = response.status

        if (status === 500 && attemptNumber < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
            return setClaims(body, attemptNumber + 1)
        }
        throw response.data
    }
}

export {
    status,
    orderBy,
    sponsors,
    getSettlements,
    getStatusText,
    getSponsorText,
    getSettlementDetail,
    sanitizeAmount,
    getPendingAuthorizations,
    setSettlementId,
    setClaims
}